import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MainContainer from "../../components/MainContainer";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import debounce from "lodash.debounce";

import Pagination from "../../components/Pagination/Pagination";
import {
  Button,
  CardTitle,
  CategoryItem,
  CategoryText,
  Column,
  Container,
  Content,
  ContentContainer,
  CreatePlaylistCard,
  EmptyState,
  FilterContainer,
  PaginationWrapper,
  SearchFilter,
  StyledInput,
} from "./PlaylistPage.styles";
import { selectLoggedIn } from "../../store/slices/auth/slice";

import { ReactComponent as BookmarkAddIcon } from "../../assets/icons/bookmark-add.svg";
import { ReactComponent as FavoritesIcon } from "../../assets/icons/favorites.svg";
import { ReactComponent as UserCircleIcon } from "../../assets/icons/userCircle.svg";
import { ReactComponent as ChevronIcon } from "../../assets/icons/chevronLeft.svg";

import { useMediaQuery } from "react-responsive";

import VideoMasonrySkeletonLoader from "../../components/utils/VideoMasonrySkeletonLoader";
import PlaylistItem from "../../components/utils/PlaylistItem/PlaylistItem";
import CreatePlaylistModal from "../../components/CreatePlaylistModal/CreatePlaylistModal";
import {
  selectPlaylists,
  setSelectedPlaylistFilter,
  setSelectedPlaylistSearch,
} from "../../store/slices/playlist/slice";
import { fetchPlaylists } from "../../store/slices/playlist/asyncThunk";
import { setSignUpModalOpen } from "../../store/slices/common/slice";

import LottieIcon from "../../components/LottieIcon/LottieIcon";
import searchAnimation from "../../assets/animations/searchToX.json";

const PLAYLIST_FILTERS = {
  FAVORITE: "FAVORITE",
  MY_PLAYLISTS: "MY_PLAYLISTS",
  MOST_FAVORITED: "MOST_FAVORITED",
  MOST_POPULAR: "MOST_POPULAR",
};

const PlaylistPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;

  const [isCreatePlaylistModalOpen, setIsCreatePlaylistModalOpen] =
    useState(false);

  const inputRef = useRef();

  const loggedIn = useSelector(selectLoggedIn);
  const { data, loading, total, filter, search } = useSelector(selectPlaylists);
  const searchDisabled = loggedIn === false;

  const [searchValue, setSearchValue] = useState(() => search ?? "");
  const [isSearchExpanded, setIsSearchExpanded] = useState(
    !!searchValue ?? false,
  );

  const isDesktop = useMediaQuery({ maxWidth: 1224 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const numOfColumns = useMemo(() => {
    switch (true) {
      case isMobile:
        return 2;
      case isTable:
        return 3;
      case isDesktop:
        return 4;
      default:
        return 5;
    }
  }, [isDesktop, isTable, isMobile]);

  const error = false;

  const handleDisabledSearchClick = useCallback(
    (e) => {
      if (!searchDisabled) return false;

      e?.preventDefault();
      e?.stopPropagation();
      inputRef.current?.blur();
      dispatch(setSignUpModalOpen(true));
      return true;
    },
    [dispatch, searchDisabled],
  );

  const changeHandler = useCallback((text) => {
    if (searchDisabled) return;

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("page");
      return newParams;
    });

    setSearchValue(text);
    dispatch(setSelectedPlaylistSearch(text));

  }, [dispatch, searchDisabled, setSearchParams]);

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 500),
    [changeHandler],
  );

  useEffect(() => {
    return () => debouncedChangeHandler.cancel();
  }, [debouncedChangeHandler]);

  useEffect(() => {
    dispatch(
      fetchPlaylists({
        page,
        search: searchDisabled ? "" : search,
        filter,
      }),
    );
  }, [dispatch, page, search, searchDisabled, filter]);

  useEffect(() => {
    if (!searchDisabled) return;

    debouncedChangeHandler.cancel();
    setIsSearchExpanded(false);

    if (searchValue) {
      setSearchValue("");
    }

    if (search) {
      dispatch(setSelectedPlaylistSearch(""));
    }
  }, [
    debouncedChangeHandler,
    dispatch,
    search,
    searchDisabled,
    searchValue,
  ]);

  useEffect(() => {
    if (isSearchExpanded && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 50);
    }
  }, [isSearchExpanded]);

  const setPage = (page) => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", page);
      return newParams;
    });
  };

  if (loggedIn === null) {
    return null;
  }

  const onSearchChange = (value) => {
    if (searchDisabled) return;

    setSearchValue(value);
    debouncedChangeHandler(value);
  };

  const onChangeFilter = (newFilter) => {
    dispatch(setSelectedPlaylistFilter(newFilter));
    searchParams.delete("page");
    setSearchParams(searchParams);
  };

  return (
    <MainContainer offPadding hideTabs={false} fullWidth>
      <Container>
        <FilterContainer>
          <CategoryItem $width={36} $noPadding onClick={() => navigate("/")}>
            <ChevronIcon />
          </CategoryItem>

          <SearchFilter
            $expanded={isSearchExpanded}
            onMouseDown={handleDisabledSearchClick}
          >
            <LottieIcon
              animationData={searchAnimation}
              trigger="toggle-reverse"
              isActive={isSearchExpanded}
              strokeColor="#fff"
              absolute
              speed={2}
              onClick={() => {
                if (handleDisabledSearchClick()) return;

                if (isSearchExpanded) {
                  setIsSearchExpanded(false);
                  onSearchChange("");
                  inputRef.current?.blur();
                } else {
                  setIsSearchExpanded(true);
                  inputRef.current?.focus();
                }
              }}
            />

            <StyledInput
              ref={inputRef}
              value={searchValue}
              placeholder={"title, username or tags"}
              readOnly={searchDisabled}
              onFocus={() => {
                if (handleDisabledSearchClick()) {
                  return;
                }

                setIsSearchExpanded(true);
              }}
              onBlur={() => {
                if (!searchValue) {
                  setTimeout(() => {
                    setIsSearchExpanded(false);
                  }, 100);
                }
              }}
              onChange={(e) => onSearchChange(e.target.value)}
            />

            {/* <CloseIconWrapper
              onClick={() => {
                if (!isSearchExpanded) {
                  inputRef.current.focus();
                }
                setIsSearchExpanded(!isSearchExpanded);
                onSearchChange("");
              }}
              $expanded={isSearchExpanded}
            >
              {isSearchExpanded && <CloseIcon />}
            </CloseIconWrapper> */}
          </SearchFilter>
          {loggedIn && (
            <CategoryItem
              $selected={filter === PLAYLIST_FILTERS.FAVORITE}
              $width={44}
              onClick={() => onChangeFilter(PLAYLIST_FILTERS.FAVORITE)}
            >
              <FavoritesIcon color={"#fff"} />
            </CategoryItem>
          )}
          {loggedIn && (
            <CategoryItem
              $selected={filter === PLAYLIST_FILTERS.MY_PLAYLISTS}
              $width={44}
              onClick={() => {
                onChangeFilter(PLAYLIST_FILTERS.MY_PLAYLISTS);
              }}
            >
              <UserCircleIcon color={"#fff"} />
            </CategoryItem>
          )}
          <CategoryItem
            $selected={filter === PLAYLIST_FILTERS.MOST_FAVORITED}
            onClick={() => {
              onChangeFilter(PLAYLIST_FILTERS.MOST_FAVORITED);
            }}
          >
            <CategoryText>Most Favorited</CategoryText>
          </CategoryItem>
          <CategoryItem
            $selected={filter === PLAYLIST_FILTERS.MOST_POPULAR}
            onClick={() => {
              onChangeFilter(PLAYLIST_FILTERS.MOST_POPULAR);
            }}
          >
            <CategoryText>Most Popular</CategoryText>
          </CategoryItem>
        </FilterContainer>

        <ContentContainer>
          {error ? (
            <Content>
              <EmptyState>
                <h3>We’re having trouble loading videos</h3>
                <p>
                  This is usually temporary. Please check your connection and
                  try again.
                </p>
                <Button
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  Retry
                </Button>
              </EmptyState>
            </Content>
          ) : loading || !data ? (
            <Content>
              <VideoMasonrySkeletonLoader
                count={24}
                fullWidth
                playlistPage
                mobileTwoRows
              />
            </Content>
          ) : (
            <Content>
              {!data.length ? (
                filter === PLAYLIST_FILTERS.MY_PLAYLISTS ? (
                  Array.from({ length: numOfColumns }).map((_, colIndex) => {
                    return (
                      <Column key={colIndex}>
                        {colIndex === 0 && (
                          <CreatePlaylistCard
                            onClick={() => {
                              setIsCreatePlaylistModalOpen(true);
                            }}
                          >
                            <BookmarkAddIcon width={130} height={130} />
                            <CardTitle>Create your own playlist</CardTitle>
                          </CreatePlaylistCard>
                        )}
                      </Column>
                    );
                  })
                ) : (
                  <EmptyState>
                    <h3>No playlists found</h3>
                    <p>There are no playlists to display at the moment.</p>
                  </EmptyState>
                )
              ) : (
                Array.from({ length: numOfColumns }).map((_, colIndex) => {
                  return (
                    <Column key={colIndex}>
                      {filter === PLAYLIST_FILTERS.MY_PLAYLISTS &&
                        colIndex === 0 && (
                          <CreatePlaylistCard
                            onClick={() => {
                              setIsCreatePlaylistModalOpen(true);
                            }}
                          >
                            <BookmarkAddIcon width={130} height={130} />
                            <CardTitle>Create your own playlist</CardTitle>
                          </CreatePlaylistCard>
                        )}
                      {data
                        .filter(
                          (_, i) =>
                            (filter === PLAYLIST_FILTERS.MY_PLAYLISTS
                              ? i + 1
                              : i) %
                              numOfColumns ===
                            colIndex,
                        )
                        .map((item) => {
                          return (
                            <PlaylistItem
                              key={item._id}
                              playlist={item}
                              onPlaylistItemClick={() => {
                                if (loggedIn) {
                                  window.scrollTo({
                                    top: 0,
                                    left: 0,
                                    behavior: "instant",
                                  });
                                  navigate(`/playlist-detail/${item._id}`);
                                } else {
                                  dispatch(setSignUpModalOpen(true));
                                }
                              }}
                            />
                          );
                        })}
                    </Column>
                  );
                })
              )}
            </Content>
          )}
        </ContentContainer>
        <PaginationWrapper>
          <Pagination
            currentPage={page}
            totalCount={total}
            perPage={20}
            onPageChange={(page) => setPage(page)}
          />
        </PaginationWrapper>
      </Container>

      <CreatePlaylistModal
        isOpen={isCreatePlaylistModalOpen}
        onClose={() => setIsCreatePlaylistModalOpen(false)}
      />
    </MainContainer>
  );
};

export default PlaylistPage;
